import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { componentStyles } from '@/lib/theme'
import useIsMobile from '@/hooks/useIsMobile'

interface LoginSignupFormProps {
  error: string | null
  isLoading: boolean
  showPasswordConfirm: boolean
  onLogin: (form: HTMLFormElement) => void
  onSignup: (form: HTMLFormElement) => void
  onForgotPassword: () => void
  onBack?: () => void
  tosAccepted: boolean
  onTosAcceptedChange: (accepted: boolean) => void
  onOpenTos: () => void
}

export default function LoginSignupForm({
  error,
  isLoading,
  showPasswordConfirm,
  onLogin,
  onSignup,
  onForgotPassword,
  onBack,
  tosAccepted,
  onTosAcceptedChange,
  onOpenTos,
}: LoginSignupFormProps) {
  const { t } = useTranslation()
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()

  // Mobile styles - keep comfortable sizes for touch
  const mobileInputStyle = isMobile ? {
    fontSize: '14px',
    padding: '10px 12px',
  } : {}

  const mobileLabelStyle = isMobile ? {
    fontSize: '12px',
    minWidth: '70px',
  } : {}

  const mobileButtonStyle = isMobile ? {
    fontSize: '12px',
    padding: '10px 16px',
  } : {}

  return (
    <div
      style={{
        animation: 'authFadeIn 0.3s ease-out',
      }}
    >
      <style>
        {`
          @keyframes authFadeIn {
            from {
              opacity: 0;
              transform: translateY(-5px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <p style={{
        ...componentStyles.authWindow.heading,
        color: colors.secondaryText,
        marginTop: isMobile ? '8px' : '12px',
        marginBottom: isMobile ? '12px' : '20px',
        fontSize: isMobile ? '14px' : undefined,
      }}>
        <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔐')}</span> {t('auth.loginOrSignup')} <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('✨')}</span>
      </p>
      <form
        style={{
          ...componentStyles.authWindow.form,
          gap: isMobile ? '12px' : '18px',
        }}
        onSubmit={(e) => {
          e.preventDefault()
          if (isLoading) return
          const form = e.currentTarget
          if (showPasswordConfirm) {
            onSignup(form)
          } else {
            onLogin(form)
          }
        }}
      >
        <div style={componentStyles.authWindow.formRow}>
          <label htmlFor="email" style={{ ...componentStyles.authWindow.label, color: colors.primaryText, ...mobileLabelStyle }}>
            {t('auth.email')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            style={{
              ...componentStyles.authWindow.input,
              border: `1px solid ${colors.primaryBorder}`,
              background: colors.primaryBg,
              color: colors.primaryText,
              ...mobileInputStyle,
            }}
          />
        </div>
        <div style={componentStyles.authWindow.formRow}>
          <label htmlFor="password" style={{ ...componentStyles.authWindow.label, color: colors.primaryText, ...mobileLabelStyle }}>
            {t('auth.password')}
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete={showPasswordConfirm ? 'new-password' : 'current-password'}
            required
            style={{
              ...componentStyles.authWindow.input,
              border: `1px solid ${colors.primaryBorder}`,
              background: colors.primaryBg,
              color: colors.primaryText,
              ...mobileInputStyle,
            }}
          />
        </div>
        {showPasswordConfirm && (
          <div style={componentStyles.authWindow.formRow}>
            <label htmlFor="confirmPassword" style={{ ...componentStyles.authWindow.label, color: colors.primaryText, ...mobileLabelStyle }}>
              {t('auth.confirm')}
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              style={{
                ...componentStyles.authWindow.input,
                border: `1px solid ${colors.primaryBorder}`,
                background: colors.primaryBg,
                color: colors.primaryText,
                ...mobileInputStyle,
              }}
            />
          </div>
        )}
        {showPasswordConfirm && (
          <div style={{
            ...componentStyles.authWindow.formRow,
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
          }}>
            <input
              id="tosAccepted"
              name="tosAccepted"
              type="checkbox"
              checked={tosAccepted}
              onChange={(e) => onTosAcceptedChange(e.target.checked)}
              required
              style={{
                width: isMobile ? '14px' : '16px',
                height: isMobile ? '14px' : '16px',
                cursor: 'pointer',
                accentColor: colors.visualAccent1,
                flexShrink: 0,
              }}
            />
            <label htmlFor="tosAccepted" style={{
              color: colors.primaryText,
              fontSize: isMobile ? '11px' : '13px',
              cursor: 'pointer',
              margin: 0,
              display: 'inline',
              lineHeight: '1.5',
            }}>
              I agree to the{' '}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  onOpenTos()
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: colors.primaryAccent,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                  fontSize: isMobile ? '11px' : '13px',
                  display: 'inline',
                  verticalAlign: 'baseline',
                }}
              >
                Terms of Service
              </button>
            </label>
          </div>
        )}
        {error && (
          <div style={{
            ...componentStyles.authWindow.messageContainer,
            marginTop: '4px',
            marginBottom: '4px',
          }}>
            <p style={{ ...componentStyles.authWindow.errorMessage, color: colors.errorBorder, fontSize: isMobile ? '11px' : undefined }}>{error}</p>
          </div>
        )}
        <div style={componentStyles.authWindow.buttonContainer}>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              if (showPasswordConfirm && onBack) {
                onBack()
              } else {
                const form = e.currentTarget.closest('form')
                if (form) onLogin(form)
              }
            }}
            disabled={isLoading}
            style={isLoading ? {
              ...componentStyles.authWindow.buttonLoading,
              background: colors.secondaryBg,
              color: colors.tertiaryText,
              border: `1px solid ${colors.primaryBorder}`,
              ...mobileButtonStyle,
            } : {
              ...componentStyles.authWindow.button,
              background: colors.secondaryBg,
              color: colors.primaryText,
              border: `1px solid ${colors.primaryBorder}`,
              ...mobileButtonStyle,
            }}
          >
            {isLoading
              ? t('auth.processing')
              : (showPasswordConfirm ? t('auth.goBack') : t('auth.login.submit'))}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              const form = e.currentTarget.closest('form')
              if (form) {
                onSignup(form)
              }
            }}
            disabled={isLoading}
            style={isLoading ? {
              ...componentStyles.authWindow.buttonSecondaryLoading,
              background: colors.secondaryBg,
              color: colors.tertiaryText,
              border: `1px solid ${colors.primaryBorder}`,
              ...mobileButtonStyle,
            } : {
              ...componentStyles.authWindow.buttonSecondary,
              background: colors.primaryBg,
              color: colors.primaryText,
              border: `1px solid ${colors.primaryBorder}`,
              ...mobileButtonStyle,
            }}
          >
            {isLoading
              ? t('auth.processing')
              : (showPasswordConfirm ? t('auth.confirmSignup') : t('auth.signup.submit'))}
          </button>
        </div>
        <div style={componentStyles.authWindow.linkButtonContainer}>
          <button
            type="button"
            onClick={onForgotPassword}
            style={{ ...componentStyles.authWindow.linkButton, color: colors.tertiaryText, fontSize: isMobile ? '11px' : undefined }}
          >
            {t('auth.resetPasswordLink')}
          </button>
        </div>
      </form>
    </div>
  )
}
